'use client';

import { withBase } from '../lib/brand';
import Link from 'next/link';
import React from 'react';
import { FLEET, type Vehicle } from '../campervan-hire/vehicleUtils';

// Image-led van-hire cross-sell, matching the store's product-page banner:
// three real fleet vans from brands we compare + a CTA. Whole card links into
// the on-site /campervan-hire guide (the DriveAway booking handoff lives there).
const HIRE_HREF = withBase('/campervan-hire');

// Show recognisable, visually distinct brands first; fall back to whatever the
// fleet has so the strip never renders short if the feed changes.
const PREFERRED_BRANDS = ['Britz', 'Maui', 'Jucy', 'Apollo', 'Mighty', 'Cheapa Campa', 'Star RV'];

function pickThree(): Vehicle[] {
  const firstByBrand = new Map<string, Vehicle>();
  for (const v of FLEET.vehicles) {
    if (v.image && !firstByBrand.has(v.brand)) firstByBrand.set(v.brand, v);
  }
  const picks: Vehicle[] = [];
  for (const brand of PREFERRED_BRANDS) {
    const v = firstByBrand.get(brand);
    if (v && !picks.includes(v)) picks.push(v);
    if (picks.length === 3) return picks;
  }
  for (const v of Array.from(firstByBrand.values())) {
    if (!picks.includes(v)) picks.push(v);
    if (picks.length === 3) break;
  }
  return picks;
}

const typeLabel = (cls: string) => (cls === 'small' ? 'Campervan' : 'Motorhome');

function trackPromoClick() {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.('event', 'select_promotion', {
    creative_name: 'van_hire_strip',
    creative_slot: 'campsite_detail_below_routes',
    promotion_id: 'campervan_hire',
    promotion_name: 'Campervan & 4WD hire',
  });
}

export default function VanHireStrip() {
  const vans = pickThree();
  if (vans.length < 3) return null;

  return (
    <Link
      href={HIRE_HREF}
      onClick={trackPromoClick}
      aria-label="Compare campervan, motorhome and 4WD hire across the major rental brands"
      className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-lg md:flex-row"
    >
      {/* Fleet tiles */}
      <div className="grid grid-cols-3 gap-2.5 bg-[#f3f0ee] p-4 md:w-[55%]">
        {vans.map((v) => (
          <span
            key={v.name}
            className="flex flex-col items-center gap-2 rounded-xl bg-white p-3 ring-1 ring-black/5"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={v.image}
              alt={`${v.name} for hire`}
              className="h-16 w-full object-contain transition-transform duration-500 group-hover:scale-105 sm:h-20"
              loading="lazy"
            />
            <span className="text-center leading-tight">
              <span
                className="block text-[12px] font-semibold text-[#1c1e20]"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {v.name}
              </span>
              <span className="text-[9px] font-semibold uppercase tracking-[0.07em] text-slate-400">
                {typeLabel(v.class)}
              </span>
            </span>
          </span>
        ))}
      </div>

      {/* Copy */}
      <div className="flex flex-1 flex-col justify-center p-5 sm:p-6">
        <div className="mb-2 flex items-center gap-2">
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1a68d1"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 17h1m16 0h1M6 17a2 2 0 1 0 4 0m4 0a2 2 0 1 0 4 0M4 17V7a1 1 0 0 1 1-1h10l4 5h1a1 1 0 0 1 1 1v5" />
            <path d="M15 6v5h4" />
          </svg>
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1a68d1]">
            Campervan &amp; motorhome hire
          </span>
        </div>
        <h3
          className="text-lg leading-tight text-[#1c1e20] sm:text-xl"
          style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}
        >
          Hitting the road? Rent the rig, not just the gear.
        </h3>
        <p className="mt-1.5 max-w-[48ch] text-[13.5px] leading-snug text-slate-600">
          Compare live prices across{' '}
          <span className="font-semibold text-[#1c1e20]">Britz, Maui, Apollo, Jucy &amp; more</span> —
          campervans and motorhomes, one-way hires included.
        </p>
        <span
          className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#1a68d1] px-4 py-2 text-[13px] font-semibold text-white transition-colors duration-300 group-hover:bg-[#1c1e20]"
        >
          Compare &amp; book
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:translate-x-0.5"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </Link>
  );
}
